import React from 'react';
import styled, { keyframes, css, CSSObject } from 'styled-components';
import theme from '^/theme';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const shimmerAnimation = css`
  background: linear-gradient(
    90deg,
    ${theme.colors.coolGray[50]} 0%,
    ${theme.colors.coolGray[100]} 50%,
    ${theme.colors.coolGray[50]} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const afterShimmerAnimation = css`
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${theme.colors.coolGray[100]} 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonWrapper = styled.div({
  backgroundColor: theme.colors.white[100],
  padding: '8px 6px',
  border: `1px solid ${theme.colors.coolGray[100]}`,
  borderRadius: '8px',
});

const SkeletonContainer = styled.div<{ containerCustomStyles?: CSSObject }>(
  ({ containerCustomStyles }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '20px',
    ...containerCustomStyles,
  })
);

const DateSkeleton = styled.div`
  height: 16px;
  width: 120px;
  border-radius: 4px;
  margin-bottom: 8px;
  ${shimmerAnimation}
`;

const ItemSkeleton = styled.div`
  display: flex;
  height: 120px;
  border-radius: 8px;
  gap: 8px;
`;

const IconSkeleton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  position: absolute;
  top: 6px;
  left: 6px;
  ${shimmerAnimation}
`;

const PhotoSkeleton = styled.div`
  position: relative;
  flex: 1;
  background-color: ${theme.colors.coolGray[50]};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  overflow: hidden;

  &::after {
    ${afterShimmerAnimation}
  }
`;

const ScrollableContainer = styled.div({
  flex: 1,
  overflowY: 'auto',
  '&>div': {
    marginBottom: '10px',
  },
});

interface SkeletonLoaderProps {
  count: number;
  containerCustomStyles?: CSSObject;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ count, containerCustomStyles }) => (
  <SkeletonContainer containerCustomStyles={containerCustomStyles}>
    <ScrollableContainer>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonWrapper key={index}>
          <DateSkeleton />
          <ItemSkeleton key={index}>
            <PhotoSkeleton>
              <IconSkeleton />
            </PhotoSkeleton>
            <PhotoSkeleton>
              <IconSkeleton />
            </PhotoSkeleton>
          </ItemSkeleton>
        </SkeletonWrapper>
      ))}
    </ScrollableContainer>
  </SkeletonContainer>
);
